import { useCallback, useEffect } from 'react';

import {
  BridgeRequestMethod,
  FullMessage,
  IGetInventoryConfig,
  InventoryAdded,
  InventoryDeleted,
  InventoryTree,
} from '@scaleaq/sitecontrol-lib';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { subscribedToEvent, useBridgeEventSubscription } from '../registerAndSubscribe';
import { useBridgeEvents } from './useBridgeEvents.ts';

export const useAllSensors = () => {
  const queryClient = useQueryClient();
  // const queryKeys = useMemo<string[]>(() => ['all-sensors'], []);

  const invalidate = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['all-sensors'] });
  }, [queryClient]);
  const { subscribeToEvent: getAllSensors, isLoading } = useBridgeEventSubscription(
    BridgeRequestMethod.GetInventory
  );

  const getter = useCallback(async () => {
    const getAllSensorsArgs: IGetInventoryConfig = {
      inventoryId: undefined,
    };
    const sensors = await getAllSensors?.(getAllSensorsArgs);
    return sensors || [];
  }, [getAllSensors]);

  // onAdded
  const { result: onAdded, isLoading: isOnAddedLoading } = useBridgeEvents<undefined, FullMessage>(
    [InventoryAdded.className],
    (id: string, eventName, data) => {
      return { id, eventName, data } as unknown as FullMessage;
    }
  );

  useEffect(() => {
    if (!isOnAddedLoading && onAdded.InventoryAdded) {
      invalidate().then(() => {});
    }
  }, [invalidate, isOnAddedLoading, onAdded.InventoryAdded]);

  // onDeleted
  const { result: onDeleted, isLoading: isOnDeletedLoading } = useBridgeEvents<undefined, string>(
    [InventoryDeleted.className],
    (id: string) => id,
    true
  );

  useEffect(() => {
    const { InventoryDeleted } = onDeleted;
    // InventoryDeleted is the id
    if (InventoryDeleted && !isOnDeletedLoading) {
      const key = Object.keys(subscribedToEvent).find(key => key.includes(InventoryDeleted));

      if (key) {
        delete subscribedToEvent[key];
      }
      invalidate().then(() => {});
    }
  }, [invalidate, isOnDeletedLoading, onDeleted]);

  return useQuery<InventoryTree[]>({
    enabled: !isLoading,
    queryKey: ['all-sensors'],
    queryFn: getter,
  });
};
